"use client";

import { useState, type ReactNode } from "react";
import * as Popover from "@radix-ui/react-popover";
import * as Tooltip from "@radix-ui/react-tooltip";
import { Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface InfoTooltipProps {
  content: ReactNode;
  label?: string;
  className?: string;
  panelClassName?: string;
}

export function InfoTooltip({ content, label = "More information", className, panelClassName }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const panel = cn("z-[80] max-w-[calc(100vw-2rem)] w-72 rounded-xl border border-border bg-popover px-3 py-2 text-left text-xs leading-5 text-popover-foreground shadow-shell", panelClassName);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Tooltip.Provider delayDuration={350}>
        <Tooltip.Root open={open ? false : undefined}>
          <Popover.Trigger asChild>
            <Tooltip.Trigger asChild>
              <button type="button" aria-label={label} className={cn("inline-flex size-11 shrink-0 items-center justify-center rounded-full border border-white/15 text-fg-secondary hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring", className)}>
                <Info className="size-4" aria-hidden="true" />
              </button>
            </Tooltip.Trigger>
          </Popover.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content sideOffset={6} collisionPadding={16} className={panel}>{content}</Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
      <Popover.Portal>
        <Popover.Content sideOffset={6} collisionPadding={16} aria-label={label} className={panel}>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">{content}</div>
            <Popover.Close aria-label="Close information" className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg focus-visible:outline-2 focus-visible:outline-ring"><X className="size-4" /></Popover.Close>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
